"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.OutputManager = void 0;
const sdk_geometry_api_sdk_v2_1 = require("@shapediver/sdk.geometry-api-sdk-v2");
const viewer_shared_services_1 = require("@shapediver/viewer.shared.services");
const viewer_shared_types_1 = require("@shapediver/viewer.shared.types");
const Output_1 = require("../dto/Output");
const OutputDelayException_1 = require("../OutputDelayException");
const OutputLoader_1 = require("../OutputLoader");
const SessionData_1 = require("../SessionData");
const SessionTreeNode_1 = require("../SessionTreeNode");
/**
 * Manager responsible for outputs.
 *
 * The manager is created by the SessionEngineCore and can be accessed
 * via the `outputManager` property.
 */
class OutputManager {
    constructor(sessionEngineCore) {
        this._eventEngine = viewer_shared_services_1.EventEngine.instance;
        this._logger = viewer_shared_services_1.Logger.instance;
        this._outputs = {};
        this._outputsFreeze = {};
        this._stateEngine = viewer_shared_services_1.StateEngine.instance;
        this._uuidGenerator = viewer_shared_services_1.UuidGenerator.instance;
        this._allowOutputLoading = true;
        this._outputLoader = new OutputLoader_1.OutputLoader(this);
        this._sessionEngineCore = sessionEngineCore;
    }
    init() {
        // Initialization logic here
    }
    get allowOutputLoading() {
        return this._allowOutputLoading;
    }
    set allowOutputLoading(value) {
        this._allowOutputLoading = value;
    }
    get jwtToken() {
        return this._sessionEngineCore.jwtToken;
    }
    get loadSdtf() {
        return this._sessionEngineCore.loadSdtf;
    }
    get outputs() {
        return this._outputs;
    }
    get outputsFreeze() {
        return this._outputsFreeze;
    }
    get reloadSdtf() {
        return this._outputLoader.reloadSdtf;
    }
    set reloadSdtf(value) {
        this._outputLoader.reloadSdtf = value;
    }
    /**
     * Create outputs from the response DTO.
     */
    createOutputsFromDto() {
        if (!this._sessionEngineCore.responseDto)
            return;
        for (const outputId in this._sessionEngineCore.responseDto.outputs) {
            if (!this.outputs[outputId]) {
                this._sessionEngineCore.responseDto.outputs[outputId].id =
                    outputId;
                if (this.outputsFreeze[outputId] === undefined)
                    this.outputsFreeze[outputId] = false;
                this.outputs[outputId] = new Output_1.Output(this._sessionEngineCore.responseDto.outputs[outputId], this._sessionEngineCore, this);
            }
            else {
                this.outputs[outputId].updateOutputDefinition((this._sessionEngineCore.responseDto.outputs[outputId]));
            }
        }
    }
    getCurrentOutputVersions() {
        return this._outputLoader.getCurrentOutputVersions();
    }
    /**
     * Load cached outputs in parallel.
     *
     * @param outputMapping mapping of output ids to their versions
     * @param taskEventInfo optional task event info
     * @param retry whether to retry the request in case of failure
     * @returns promise with a mapping of output ids to their scene graph nodes
     */
    loadCachedOutputsParallel(outputMapping, taskEventInfo, retry = false) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            this._sessionEngineCore.utilsManager.checkAvailability();
            // if there is already task event info, use it
            // this happens after a retry
            const eventInfo = {
                type: taskEventInfo
                    ? taskEventInfo.type
                    : viewer_shared_types_1.TASK_TYPE.SESSION_OUTPUTS_LOADING,
                category: taskEventInfo ? taskEventInfo.category : undefined,
                id: taskEventInfo ? taskEventInfo.id : this._uuidGenerator.create(),
                data: taskEventInfo
                    ? taskEventInfo.data
                    : { sessionId: this._sessionEngineCore.id },
                progressRange: {
                    min: 0,
                    max: 1,
                },
            };
            try {
                // send start event if this function was called initially
                if (!taskEventInfo) {
                    this._eventEngine.emitEvent(viewer_shared_services_1.EVENTTYPE.TASK.TASK_START, Object.assign(Object.assign({}, eventInfo), { progress: 0, status: "Loading cached outputs" }));
                }
                // get the cached outputs
                const responseDto = (yield new sdk_geometry_api_sdk_v2_1.OutputApi(this._sessionEngineCore.sdkConfig).getCachedOutputs(this._sessionEngineCore.sessionId, outputMapping)).data;
                // create atomic output api objects for them
                const outputs = {};
                for (const outputId in responseDto.outputs) {
                    responseDto.outputs[outputId].id = outputId;
                    outputs[outputId] = new Output_1.Output(responseDto.outputs[outputId], this._sessionEngineCore, this);
                }
                // process the output data
                const node = yield this._outputLoader.loadOutputs(((_a = this._sessionEngineCore.responseDto.model) === null || _a === void 0 ? void 0 : _a.name) || "model", outputs, {}, eventInfo, false);
                // send the end event once done
                this._eventEngine.emitEvent(viewer_shared_services_1.EVENTTYPE.TASK.TASK_END, Object.assign(Object.assign({}, eventInfo), { progress: 1, status: "Loaded cached outputs" }));
                // create a mapping with a dictionary for the id of the outputs
                const outputNodeMapping = {};
                for (const outputId in outputMapping)
                    outputNodeMapping[outputId] = node.children.find((n) => n.name === outputId);
                return outputNodeMapping;
            }
            catch (e) {
                yield this._sessionEngineCore.utilsManager.handleError(e, retry);
                return yield this.loadCachedOutputsParallel(outputMapping, eventInfo, true);
            }
        });
    }
    /**
     * Load the outputs and return the scene graph node of the result.
     * In case the outputs have a delay property, another customization request with the parameter set is sent.
     *
     * @param parameters the parameter set to update the session
     * @param outputs the outputs to load
     * @returns promise with a scene graph node
     */
    loadOutputs(cancelRequest = () => false, taskEventInfo, retry = false) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            this._sessionEngineCore.utilsManager.checkAvailability();
            const o = Object.assign({}, this.outputs);
            const of = Object.assign({}, this.outputsFreeze);
            try {
                const node = yield this._outputLoader.loadOutputs(((_a = this._sessionEngineCore.responseDto.model) === null || _a === void 0 ? void 0 : _a.name) || "model", o, of, taskEventInfo);
                node.data.push(new SessionData_1.SessionData(this._sessionEngineCore.responseDto));
                if (cancelRequest())
                    return node;
                node.excludeViewports = JSON.parse(JSON.stringify(this._sessionEngineCore.excludeViewports));
                return node;
            }
            catch (e) {
                if (e instanceof OutputDelayException_1.OutputDelayException) {
                    yield this._sessionEngineCore.utilsManager.timeout(e.delay);
                }
                else {
                    yield this._sessionEngineCore.utilsManager.handleError(e, retry);
                    if (cancelRequest())
                        return new SessionTreeNode_1.SessionTreeNode();
                    return yield this.loadOutputs(cancelRequest, taskEventInfo, true);
                }
                if (cancelRequest())
                    return new SessionTreeNode_1.SessionTreeNode();
                const outputMapping = {};
                for (const output in o)
                    outputMapping[output] = o[output].version;
                try {
                    const responseDto = (yield new sdk_geometry_api_sdk_v2_1.OutputApi(this._sessionEngineCore.sdkConfig).getCachedOutputs(this._sessionEngineCore.sessionId, outputMapping)).data;
                    if (cancelRequest())
                        return new SessionTreeNode_1.SessionTreeNode();
                    this._sessionEngineCore.updateResponseDto(responseDto);
                    return yield this.loadOutputs(cancelRequest, taskEventInfo);
                }
                catch (e) {
                    yield this._sessionEngineCore.utilsManager.handleError(e, retry);
                    if (cancelRequest())
                        return new SessionTreeNode_1.SessionTreeNode();
                    return yield this.loadOutputs(cancelRequest, taskEventInfo, true);
                }
            }
        });
    }
    /**
     * Load the outputs and return the scene graph node of the result.
     * In case the outputs have a delay property, another customization request with the parameter set is sent.
     *
     * @param parameters the parameter set to update the session
     * @param outputs the outputs to load
     * @returns promise with a scene graph node
     */
    loadOutputsParallel(responseDto, cancelRequest = () => false, taskEventInfo, retry = false) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            this._sessionEngineCore.utilsManager.checkAvailability();
            const outputs = {};
            const outputsFreeze = {};
            for (const outputId in responseDto.outputs) {
                responseDto.outputs[outputId].id = outputId;
                if (this.outputsFreeze[outputId] === undefined)
                    outputsFreeze[outputId] = false;
                outputs[outputId] = new Output_1.Output(responseDto.outputs[outputId], this._sessionEngineCore, this);
            }
            try {
                const node = yield this._outputLoader.loadOutputs(((_a = this._sessionEngineCore.responseDto.model) === null || _a === void 0 ? void 0 : _a.name) || "model", outputs, outputsFreeze, taskEventInfo, true, true);
                node.data.push(new SessionData_1.SessionData(responseDto));
                return node;
            }
            catch (e) {
                if (e instanceof OutputDelayException_1.OutputDelayException) {
                    yield this._sessionEngineCore.utilsManager.timeout(e.delay);
                }
                else {
                    yield this._sessionEngineCore.utilsManager.handleError(e, retry);
                    if (cancelRequest())
                        return new SessionTreeNode_1.SessionTreeNode();
                    return yield this.loadOutputsParallel(responseDto, cancelRequest, taskEventInfo, true);
                }
                if (cancelRequest())
                    return new SessionTreeNode_1.SessionTreeNode();
                const outputMapping = {};
                for (const output in outputs)
                    outputMapping[output] = outputs[output].version;
                try {
                    const responseDto = (yield new sdk_geometry_api_sdk_v2_1.OutputApi(this._sessionEngineCore.sdkConfig).getCachedOutputs(this._sessionEngineCore.sessionId, outputMapping)).data;
                    if (cancelRequest())
                        return new SessionTreeNode_1.SessionTreeNode();
                    this._sessionEngineCore.updateResponseDto(responseDto);
                    return yield this.loadOutputsParallel(responseDto, cancelRequest, taskEventInfo);
                }
                catch (e) {
                    yield this._sessionEngineCore.utilsManager.handleError(e, retry);
                    if (cancelRequest())
                        return new SessionTreeNode_1.SessionTreeNode();
                    return yield this.loadOutputsParallel(responseDto, cancelRequest, taskEventInfo, true);
                }
            }
        });
    }
    /**
     * Save the output properties for displayname, order, tooltip and hidden
     *
     * @param outputs
     * @returns
     */
    saveOutputProperties(outputs, retry = false) {
        return __awaiter(this, void 0, void 0, function* () {
            this._sessionEngineCore.utilsManager.checkAvailability("output-definition", true);
            try {
                yield new sdk_geometry_api_sdk_v2_1.OutputApi(this._sessionEngineCore.sdkConfig).updateOutputDefinitions(this._sessionEngineCore.modelId, outputs);
                return true;
            }
            catch (e) {
                yield this._sessionEngineCore.utilsManager.handleError(e, retry);
                return yield this.saveOutputProperties(outputs, true);
            }
        });
    }
    /**
     * Update the outputs and return the scene graph node of the result.
     * In case the outputs have a delay property, another customization request with the parameter set is sent.
     *
     * @param taskEventInfo optional task event info
     * @param waitForViewportUpdate whether to wait for the viewport update callbacks before returning
     * @returns promise with a scene graph node
     */
    updateOutputs(taskEventInfo, waitForViewportUpdate = false) {
        return __awaiter(this, void 0, void 0, function* () {
            const eventInfo = {
                type: taskEventInfo
                    ? taskEventInfo.type
                    : viewer_shared_types_1.TASK_TYPE.SESSION_OUTPUTS_UPDATE,
                category: taskEventInfo ? taskEventInfo.category : undefined,
                id: taskEventInfo ? taskEventInfo.id : this._uuidGenerator.create(),
                data: taskEventInfo
                    ? taskEventInfo.data
                    : { sessionId: this._sessionEngineCore.id },
                progress: 0,
            };
            const customizationId = this._uuidGenerator.create();
            if (!taskEventInfo) {
                this._eventEngine.emitEvent(viewer_shared_services_1.EVENTTYPE.TASK.TASK_START, Object.assign(Object.assign({}, eventInfo), { progress: 0, status: "Updating outputs" }));
            }
            const oldNode = this._sessionEngineCore.node;
            this._sessionEngineCore.customizationManager.customizationProcess =
                customizationId;
            this._logger.debugLow(`Session(${this._sessionEngineCore.id}).updateOutputs: Updating Outputs.`);
            this._sessionEngineCore.utilsManager.addBusyMode(customizationId);
            this._eventEngine.emitEvent(viewer_shared_services_1.EVENTTYPE.TASK.TASK_PROCESS, Object.assign(Object.assign({}, eventInfo), { progress: taskEventInfo
                    ? (taskEventInfo.progressRange.max -
                        taskEventInfo.progressRange.min) *
                        0.1 +
                        taskEventInfo.progressRange.min
                    : 0.1, status: "Loading outputs" }));
            const oldOutputVersions = this._outputLoader.getCurrentOutputVersions();
            const newNode = yield this.loadOutputs(() => this._sessionEngineCore.customizationManager
                .customizationProcess !== customizationId, Object.assign(Object.assign({}, eventInfo), { progressRange: {
                    min: taskEventInfo
                        ? (taskEventInfo.progressRange.max -
                            taskEventInfo.progressRange.min) *
                            0.1 +
                            taskEventInfo.progressRange.min
                        : 0.1,
                    max: taskEventInfo
                        ? (taskEventInfo.progressRange.max -
                            taskEventInfo.progressRange.min) *
                            0.9 +
                            taskEventInfo.progressRange.min
                        : 0.9,
                } }));
            this._sessionEngineCore.utilsManager.warningCreator(this._sessionEngineCore.responseDto.outputs, this._sessionEngineCore.responseDto.exports, this._sessionEngineCore.customizationManager
                .throwOnCustomizationError);
            const newOutputVersions = this._outputLoader.getCurrentOutputVersions();
            this._eventEngine.emitEvent(viewer_shared_services_1.EVENTTYPE.TASK.TASK_PROCESS, Object.assign(Object.assign({}, eventInfo), { progress: taskEventInfo
                    ? (taskEventInfo.progressRange.max -
                        taskEventInfo.progressRange.min) *
                        0.9 +
                        taskEventInfo.progressRange.min
                    : 0.9, status: "Updating scene" }));
            // OPTION TO SKIP - PART 1
            const cancelResult = this._sessionEngineCore.utilsManager.cancelProcess(Object.assign(Object.assign({}, eventInfo), { progress: taskEventInfo
                    ? (taskEventInfo.progressRange.max -
                        taskEventInfo.progressRange.min) *
                        1 +
                        taskEventInfo.progressRange.min
                    : 1 }), customizationId, newNode);
            if (cancelResult)
                return cancelResult;
            // call the update callbacks
            if (waitForViewportUpdate === false) {
                for (const outputId in this.outputs) {
                    if (oldOutputVersions[outputId] !== newOutputVersions[outputId]) {
                        this._eventEngine.emitEvent(viewer_shared_services_1.EVENTTYPE.OUTPUT.OUTPUT_UPDATED, {
                            outputId: outputId,
                            outputVersion: newOutputVersions[outputId],
                            newNode: newNode.children.find((c) => c.name === outputId),
                            oldNode: oldNode.children.find((c) => c.name === outputId),
                        });
                    }
                }
                yield this._sessionEngineCore.utilsManager.waitForUpdateCallbacks(newOutputVersions, oldOutputVersions, newNode, oldNode);
                // OPTION TO SKIP - PART 2
                const cancelResult = this._sessionEngineCore.utilsManager.cancelProcess(Object.assign(Object.assign({}, eventInfo), { progress: taskEventInfo
                        ? (taskEventInfo.progressRange.max -
                            taskEventInfo.progressRange.min) *
                            1 +
                            taskEventInfo.progressRange.min
                        : 1 }), customizationId, newNode);
                if (cancelResult)
                    return cancelResult;
            }
            if (this._sessionEngineCore.automaticSceneUpdate)
                this._sessionEngineCore.utilsManager.removeFromSceneTree(this._sessionEngineCore.node);
            this._sessionEngineCore.node = newNode;
            if (this._sessionEngineCore.automaticSceneUpdate &&
                this._sessionEngineCore.closed === false)
                this._sessionEngineCore.utilsManager.addToSceneTree(this._sessionEngineCore.node);
            this._logger.debug(`Session(${this._sessionEngineCore.id}).updateOutputs: Updating outputs finished, updating geometry.`);
            // set the output content to what has been updated
            for (const outputId in this.outputs) {
                this.outputs[outputId].updateOutput(newNode.children.find((c) => c.name === outputId), oldNode.children.find((c) => c.name === outputId));
            }
            // set the export definitions
            for (const exportId in this._sessionEngineCore.exportManager.exports)
                this._sessionEngineCore.exportManager.exports[exportId].updateExport();
            this._sessionEngineCore.node.excludeViewports = JSON.parse(JSON.stringify(this._sessionEngineCore.excludeViewports));
            this._sessionEngineCore.utilsManager.removeBusyMode(customizationId);
            this._logger.debug(`Session(${this._sessionEngineCore.id}).updateOutputs: Updated outputs.`);
            if (!taskEventInfo) {
                this._eventEngine.emitEvent(viewer_shared_services_1.EVENTTYPE.TASK.TASK_END, Object.assign(Object.assign({}, eventInfo), { progress: 1, status: "Outputs updated" }));
            }
            // update the viewports
            if (waitForViewportUpdate) {
                for (const r in this._stateEngine.viewportEngines)
                    if (this._stateEngine.viewportEngines[r] &&
                        !this._sessionEngineCore.excludeViewports.includes(this._stateEngine.viewportEngines[r].id))
                        this._stateEngine.viewportEngines[r].update(`SessionEngine(${this._sessionEngineCore.id}).updateOutputs`);
                for (const outputId in this.outputs) {
                    if (oldOutputVersions[outputId] !== newOutputVersions[outputId]) {
                        this._eventEngine.emitEvent(viewer_shared_services_1.EVENTTYPE.OUTPUT.OUTPUT_UPDATED, {
                            outputId: outputId,
                            outputVersion: newOutputVersions[outputId],
                            newNode: newNode.children.find((c) => c.name === outputId),
                            oldNode: oldNode.children.find((c) => c.name === outputId),
                        });
                    }
                }
                yield this._sessionEngineCore.utilsManager.waitForUpdateCallbacks(newOutputVersions, oldOutputVersions, newNode, oldNode);
                // OPTION TO SKIP - PART 3
                const cancelResult = this._sessionEngineCore.utilsManager.cancelProcess(Object.assign(Object.assign({}, eventInfo), { progress: taskEventInfo
                        ? (taskEventInfo.progressRange.max -
                            taskEventInfo.progressRange.min) *
                            1 +
                            taskEventInfo.progressRange.min
                        : 1 }), customizationId, newNode);
                if (cancelResult)
                    return cancelResult;
            }
            return this._sessionEngineCore.node;
        });
    }
}
exports.OutputManager = OutputManager;
//# sourceMappingURL=OutputManager.js.map